import {
  addDays,
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  getISOWeek,
  getISOWeekYear,
  getQuarter,
  getYear,
  parseISO,
} from 'date-fns'
import { createDateWithoutTimezone } from '@omnea-app/packages/universal/date-utils/createDateWithoutTimezone'
import { assertNever } from '@omnea-app/packages/universal/type-guards/assertNever'
import { TimeGranularity } from './granularity'

type GranularityLabelTranslate = (key: string, values: Record<string, number>) => string

/**
 * Formats a date string based on the specified granularity
 */
export const formatDateByGranularity = (
  dateString: string,
  granularity: TimeGranularity,
  t: GranularityLabelTranslate
): string => {
  const date = createDateWithoutTimezone(dateString)

  switch (granularity) {
    case 'day':
      return format(date, 'MMM d, yyyy')

    case 'week': {
      const weekNum = getISOWeek(date)
      const year = getISOWeekYear(date)
      return t('charts.axis.week', { weekNum, year })
    }

    case 'month':
      return format(date, 'MMM yyyy')

    case 'quarter': {
      const quarter = getQuarter(date)
      const year = getYear(date)
      return t('charts.axis.quarter', { quarter, year })
    }

    case 'year':
      return format(date, 'yyyy')

    default:
      assertNever(granularity)
      return dateString
  }
}

export const dayAfter = (day: string): string => format(addDays(parseISO(day), 1), 'yyyy-MM-dd')

export const getBucketDateRange = (rawTimeBucket: string, granularity: TimeGranularity): [string, string] => {
  const start = createDateWithoutTimezone(rawTimeBucket)
  const end = (() => {
    switch (granularity) {
      case 'day':
        return endOfDay(start)
      case 'week':
        return endOfWeek(start, { weekStartsOn: 1 })
      case 'month':
        return endOfMonth(start)
      case 'quarter':
        return endOfQuarter(start)
      case 'year':
        return endOfYear(start)
      default:
        assertNever(granularity)
        return start
    }
  })()
  return [format(start, 'yyyy-MM-dd'), format(end, 'yyyy-MM-dd')]
}
